import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Container,
  ContainerContent,
  ContainerHeader,
  ContainerOverview,
  MyWorkTaskTable,
} from '@/component';
import MyWorkProjectStatistics from '@/component/MyWorkTaskList/MyWorkProjectStatistics';
import { useInfiniteProejctListQuery, useProjectCountQuery } from '@/hook';
import {
  VFlex,
  VStack,
  VTab,
  VTabContent,
  VTabTrigger,
  VTabTriggerContainer,
  new__cn as cn,
  useIntersectionObserver,
} from '@/package';
import { useMainLayout } from '@/provider';
import { useAuthStore } from '@/store';

enum MY_WORK_PAGE_TAB {
  WORKS = 'works',
  REVIEWS = 'reviews',
}

enum WORKS_SECTIONS_TAB {
  LIST = 'list',
  STATISTICS = 'statistics',
}

const MyWorkPage = () => {
  const { user } = useAuthStore();
  const { setTitle } = useMainLayout();
  const [myWorkPageTag, setMyWorkPageTab] = useState<MY_WORK_PAGE_TAB>(
    MY_WORK_PAGE_TAB.WORKS,
  );
  const [worksSectionsTab, setWorksSectionsTab] = useState<WORKS_SECTIONS_TAB>(
    WORKS_SECTIONS_TAB.LIST,
  );

  const isWorksTab = myWorkPageTag === MY_WORK_PAGE_TAB.WORKS;
  const isReviewsTab = myWorkPageTag === MY_WORK_PAGE_TAB.REVIEWS;

  const projectListParams = {
    workerID: isWorksTab ? user.id : undefined,
    reviewerID: isReviewsTab ? user.id : undefined,
  };
  const {
    data: projectListData,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteProejctListQuery(projectListParams);
  const { data: count } = useProjectCountQuery({ user });
  const ref = useRef<HTMLDivElement>(null);

  const infiniteProjectList =
    projectListData?.pages
      .map(page => {
        return page.data.resultData;
      })
      ?.flat() || [];

  useIntersectionObserver(
    ref,
    () => {
      if (hasNextPage && !isFetchingNextPage) fetchNextPage();
    },
    { threshold: 0.1 },
  );

  useEffect(() => {
    setTitle('My works');
  }, []);

  const { t } = useTranslation();

  return (
    <Container className="bg-gray-100">
      <ContainerOverview>
        <VFlex className="items-center justify-center pt-6 pb-4">
          <VTab
            defaultValue={MY_WORK_PAGE_TAB.WORKS}
            value={myWorkPageTag}
            onValueChange={val => setMyWorkPageTab(val as MY_WORK_PAGE_TAB)}
          >
            <VTabTriggerContainer>
              <VTabTrigger value={MY_WORK_PAGE_TAB.WORKS}>Works</VTabTrigger>
              <VTabTrigger value={MY_WORK_PAGE_TAB.REVIEWS}>
                Reviews
              </VTabTrigger>
            </VTabTriggerContainer>
          </VTab>
        </VFlex>
      </ContainerOverview>

      <VTab
        defaultValue={WORKS_SECTIONS_TAB.LIST}
        value={isReviewsTab ? WORKS_SECTIONS_TAB.LIST : worksSectionsTab}
        onValueChange={val => setWorksSectionsTab(val as WORKS_SECTIONS_TAB)}
        className="relative z-10 flex w-full flex-1 flex-col items-center overflow-hidden"
      >
        <ContainerHeader>
          <VFlex className="w-full py-4">
            <VTabTriggerContainer>
              <VTabTrigger value={WORKS_SECTIONS_TAB.LIST}>List</VTabTrigger>
              {isWorksTab && (
                <VTabTrigger value={WORKS_SECTIONS_TAB.STATISTICS}>
                  Statistics
                </VTabTrigger>
              )}
            </VTabTriggerContainer>
          </VFlex>
        </ContainerHeader>

        <ContainerContent>
          <VStack className="h-full w-full">
            <VStack className="h-full flex-1 pb-8">
              <VTabContent value={WORKS_SECTIONS_TAB.LIST}>
                <VStack gap={1} className="h-full w-full overflow-x-hidden">
                  <div className={cn('pt-4')}>
                    {t('myWork.totalProjectListLength.interstreet', {
                      current: infiniteProjectList.length,
                      total: count ?? 0,
                    })}
                  </div>

                  <div className="min-h-0 w-full flex-1 overflow-x-hidden overflow-y-auto py-2">
                    <MyWorkTaskTable
                      projectList={infiniteProjectList}
                      ref={ref}
                    />
                  </div>
                </VStack>
              </VTabContent>

              <VTabContent value={WORKS_SECTIONS_TAB.STATISTICS}>
                <MyWorkProjectStatistics />
              </VTabContent>
            </VStack>
          </VStack>
        </ContainerContent>
      </VTab>
    </Container>
  );
};

export default MyWorkPage;
